import { LitElement, PropertyValues, css, html, nothing } from 'lit'
import { property } from 'lit/decorators.js'
import '@spectrum-web-components/bundle/elements.js'
import { translationKeys } from '../../translations'
import { currentLanguage as lang } from '../../common/i18n'
import '../time-input/time-input'

/**
 * Time Range Component with Overnight Detection
 * 
 * Features:
 * - Automatic overnight hours detection (when closing < opening time)
 * - Visual indicators for overnight periods (🌙 icon)
 * - Support for 24:00 format
 * - Toggle switch for enabling/disabling times
 * - Clear button for resetting times
 * 
 * CSS Classes:
 * - .overnight-hours: Applied when overnight hours detected
 * 
 * Data Attributes:
 * - data-overnight: "true" when overnight hours detected
 * 
 * Usage:
 * <time-range from="23:00" to="03:00" name="dinner" switch clear></time-range>
 */
export class TimeRange extends LitElement {
    @property({ type: String, reflect: true }) from = '00:00'
    @property({ type: String, reflect: true }) to = '00:00'
    @property({ type: String, reflect: true }) name: string = 'range'
    @property({ type: Boolean, reflect: true }) switch = false
    @property({ type: Boolean, reflect: true }) off = true
    @property({ type: Boolean, reflect: true }) clear = false
    @property({ type: Boolean, reflect: true, attribute: 'hide-status' }) hideStatus = false
    @property({ type: Boolean, reflect: true }) compact = false
    private initializedOffState = false

    /**
     * Getter to detect if current time range represents overnight hours
     * @returns true if this is an overnight time range (closes before opens or ends at 24:xx)
     */
    get isOvernightHours(): boolean {
        if (!this.from || !this.to || this.from === '00:00' && this.to === '00:00') {
            return false
        }
        
        // Parse times to compare
        const [fromHour, fromMin] = this.from.split(':').map(Number)
        const [toHour, toMin] = this.to.split(':').map(Number)
        
        const fromMinutes = fromHour * 60 + fromMin
        const toMinutes = toHour * 60 + toMin
        
        if (Number.isNaN(fromMinutes) || Number.isNaN(toMinutes)) {
            return false
        }

        return toMinutes < fromMinutes
    }

    private emitRangeChange() {
        this.dispatchEvent(new CustomEvent('timerangechange', {
            detail: {
                name: this.name,
                from: this.from,
                to: this.to,
                off: this.off,
            },
            bubbles: true,
            composed: true,
        }))
    }

    protected willUpdate(_changedProperties: PropertyValues): void {
        if (!this.initializedOffState) {
            this.initializedOffState = true
            if (this.from !== '00:00' || this.to !== '00:00' || this.switch === false) {
                this.off = false
            }
        }

        if (this.off === true) {
            this.from = '00:00'
            this.to = '00:00'
        }
    }

    protected async updated(_changedProperties: PropertyValues): Promise<void> {        
        // Update data attributes for styling and testing
        this.setAttribute('data-overnight', this.isOvernightHours.toString())
        
        // Add/remove CSS class for overnight styling
        if (this.isOvernightHours) {
            this.classList.add('overnight-hours')
        } else {
            this.classList.remove('overnight-hours')
        }
    }

    render() {
        const tr = translationKeys[lang as keyof typeof translationKeys]

        return html`
        <div class="time-range-container ${this.isOvernightHours ? 'overnight' : ''}">
            <div class="time-controls">
                ${ this.off
                ? html`
                ${ this.switch ? html`
                    <sp-switch label="öffnen" emphasized @change=${() => {
                        this.off = false
                        this.emitRangeChange()
                    }}>
                        <slot></slot>
                    </sp-switch>`:nothing }
                `
                : html`
                    ${ this.switch ? html`
                    <sp-switch checked label="schliessen" emphasized @change=${() => {
                        this.off = true
                        this.emitRangeChange()
                    }}>
                        <slot></slot>
                    </sp-switch>`: !this.compact ? html`<div class="whitespace"></div>` : nothing }
                    <time-input .value="${this.from}" .name="${this.name +'-start'}" @timeinput=${(evt: any)=> {

                        this.from = evt.detail
                        console.log('this new from is'+ evt.detail)
                        this.emitRangeChange() }}></time-input>
                    </span>
                    <span class="bind">
                        &ndash;
                    </span>
                    <time-input .value="${this.to}" .name="${this.name+'-end'}" @timeinput=${(evt: any)=> {
                        this.to = evt.detail
                        this.emitRangeChange() }}></time-input>
                    ${ this.clear && (this.to !== '00:00' || this.from !== '00:00') ? html`
                    <button @click=${ () => {
                        this.to = '00:00'
                        this.from = '00:00'
                        this.emitRangeChange()
                    } }>&#x2715;</button>` : html`<span class="clear-placeholder"></span>` }

                `}
            </div>
            ${ this.switch && !this.hideStatus ? html`
            <div class="status-label ${this.off ? 'closed' : 'open'}">
                ${this.off ? tr.isClosed : tr.isOpen}
            </div>` : nothing }
            ${this.isOvernightHours ? html`<div class="overnight-text">(${tr.overnightIndicator})</div>` : nothing}
        </div>
    `
    }

    static styles = css`

    :host {
      display: block;
    }

    /* Container with stable layout */
    .time-range-container {
      display: block;
      margin-bottom: 8px;
      text-align: left;
    }

    /* Overnight styling for the entire container */
    .time-range-container.overnight {
      background-color: rgba(96, 155, 226, 0.08);
      border-radius: 6px;
      padding: 8px 12px 8px 29px;
      margin: 6px -26px 12px -26px;
      border-left: 3px solid rgba(96, 155, 226, 0.3);
    }

    /* Text indicator for overnight hours */
    .overnight-text {
      font-size: 11px;
      color: #1a5490;
      font-style: italic;
      margin-top: 6px;
      text-align: left;
      padding-left: 4px;
    }

    /* Status label next to the toggle */
    .status-label {
      font-size: 12px;
      font-weight: 600;
      margin-top: 6px;
      padding: 3px 10px;
      border-radius: 10px;
      display: inline-block;
      margin-left: 0;
      text-align: left;
      vertical-align: top;
    }

    .status-label.open {
      background-color: #e8f5e9;
      color: #2e7d32;
    }

    .status-label.closed {
      background-color: #ffebee;
      color: #c62828;
    }

    /* Time controls flex container */
    .time-controls {
        display: flex;
        align-items: center;
    }

    time-input[value="00:00"] {
      opacity: 0.5;
    }

    .clear-placeholder {
        display: block;
        width: 21px;
    }

    button {
        appearance: none;
        cursor: pointer;
        padding: 2px 5px;
        border: none;
        background: transparent;
    }

    sp-switch, .whitespace {
        --spectrum-switch-background-color-selected-default: #609BE2!important;
        min-width: 90px; 
    }

    span.bind {
      display: inline-block;
      padding: 4px;
    }
    span:first-child {
      display: inline-block;
      width: 80px;
    }
  `
}

if (!customElements.get('time-range')) {
    customElements.define('time-range', TimeRange)
}

declare global {
    interface HTMLElementTagNameMap {
        'time-range': TimeRange
    }
}
